import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowLeft, Home, Zap, Wrench, SlidersHorizontal } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useHomeStore } from '@/stores/useHomeStore';
import PropertyCard from '@/components/cards/PropertyCard';
import ProductCard from '@/components/cards/ProductCard';
import ServiceCard from '@/components/cards/ServiceCard';
import { getImageUrl, extractImageUrls } from '@/config/media';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/config/supabase';

// ── helpers ───────────────────────────────────────────────────────────────────

const getPropertyImage = (p: any) =>
  getImageUrl(p.images?.[0]) || '';

const getProductImage = (p: any) => {
  if (p.image_url) return getImageUrl(p.image_url);
  return extractImageUrls(p.media_urls)[0] || '';
};

const getServiceImage = (s: any) =>
  extractImageUrls(s.media_urls)[0] || '';

type Tab = 'all' | 'properties' | 'products' | 'services';

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
  { key: 'all',        label: 'All',        icon: <SlidersHorizontal size={13} /> },
  { key: 'properties', label: 'Properties', icon: <Home size={13} /> },
  { key: 'products',   label: 'Products',   icon: <Zap size={13} /> },
  { key: 'services',   label: 'Services',   icon: <Wrench size={13} /> },
];

// ── Page ──────────────────────────────────────────────────────────────────────

export const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const {
    featuredVacancies, featuredProducts, featuredServices,
    fetchHomeData, vacanciesLoading, productsLoading, servicesLoading,
  } = useHomeStore();

  // Load data if not already loaded
  useEffect(() => {
    if (!featuredVacancies.length && !featuredProducts.length && !featuredServices.length) {
      fetchHomeData();
    }
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!user) return;
    supabase.from('user_favorites').select('item_id').eq('user_id', user.id)
      .then(({ data }) => { if (data) setFavorites(new Set(data.map((f: any) => f.item_id))); });
  }, [user]);

  const handleToggleFavorite = async (id: string, type: 'property' | 'product' | 'service') => {
    if (!user) { navigate('/login'); return; }
    const isFav = favorites.has(id);
    setFavorites(prev => { const n = new Set(prev); isFav ? n.delete(id) : n.add(id); return n; });
    if (isFav) {
      await supabase.from('user_favorites').delete().eq('user_id', user.id).eq('item_type', type).eq('item_id', id);
    } else {
      await supabase.from('user_favorites').insert({ user_id: user.id, item_type: type, item_id: id });
    }
  };

  // ── Filtering ────────────────────────────────────────────────────────────────
  const q = query.toLowerCase().trim();

  const matchedVacancies = featuredVacancies.filter(v =>
    !q ||
    v.title?.toLowerCase().includes(q) ||
    v.location?.toLowerCase().includes(q) ||
    v.type?.toLowerCase().includes(q)
  );

  const matchedProducts = featuredProducts.filter(p =>
    !q ||
    p.name?.toLowerCase().includes(q) ||
    p.category?.toLowerCase().includes(q) ||
    p.business_name?.toLowerCase().includes(q) ||
    p.description?.toLowerCase().includes(q)
  );

  const matchedServices = featuredServices.filter(s =>
    !q ||
    s.name?.toLowerCase().includes(q) ||
    s.category?.toLowerCase().includes(q) ||
    s.business_name?.toLowerCase().includes(q) ||
    s.description?.toLowerCase().includes(q)
  );

  const totalResults = matchedVacancies.length + matchedProducts.length + matchedServices.length;
  const isLoading = vacanciesLoading || productsLoading || servicesLoading;

  // ── Formatters ───────────────────────────────────────────────────────────────
  const formatProduct = (p: any) => ({
    id: p.id, name: p.name, description: p.description, price: p.price,
    category: p.category, images: [getProductImage(p)],
    business_name: p.business_name, stock_quantity: p.quantity_in_stock,
    rating: p.rating, is_featured: p.is_featured,
  });

  const formatService = (s: any) => ({
    id: s.id, title: s.name, description: s.description, price: s.price,
    category: s.category, images: [getServiceImage(s)],
    business_name: s.business_name, duration: s.duration,
    rating: s.rating, delivery_mode: s.delivery_mode,
  });

  const showProperties = activeTab === 'all' || activeTab === 'properties';
  const showProducts   = activeTab === 'all' || activeTab === 'products';
  const showServices   = activeTab === 'all' || activeTab === 'services';

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24 md:pb-8">

      {/* ── Search header ── */}
      <div className="sticky top-0 z-30 bg-light-bg dark:bg-dark-bg border-b border-light-border dark:border-dark-border px-4 pt-4 pb-3">
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-light-card dark:hover:bg-dark-card transition-colors flex-shrink-0"
          >
            <ArrowLeft size={20} className="text-light-text dark:text-dark-text" />
          </button>

          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search properties, products, services…"
              className="w-full pl-9 pr-9 py-2.5 rounded-xl
                bg-light-card dark:bg-dark-card
                border border-light-border dark:border-dark-border
                text-sm text-light-text dark:text-dark-text
                placeholder-light-placeholder dark:placeholder-dark-placeholder
                focus:outline-none focus:ring-2 focus:ring-primary/30 dark:focus:ring-golden/30"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X size={14} className="text-light-subtle dark:text-dark-subtle" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${
                activeTab === tab.key
                  ? 'bg-primary dark:bg-golden text-white dark:text-dark-bg shadow-sm'
                  : 'bg-light-card dark:bg-dark-card text-light-subtle dark:text-dark-subtle border border-light-border dark:border-dark-border'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.key !== 'all' && q && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === tab.key
                    ? 'bg-white/20 text-white dark:text-dark-bg'
                    : 'bg-primary/10 dark:bg-golden/10 text-primary dark:text-golden'
                }`}>
                  {tab.key === 'properties' ? matchedVacancies.length
                    : tab.key === 'products' ? matchedProducts.length
                    : matchedServices.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Results ── */}
      <div className="px-4 md:px-6 pt-4">

        {/* Result count */}
        {q && !isLoading && (
          <p className="text-xs text-light-subtle dark:text-dark-subtle mb-4">
            {totalResults === 0
              ? `No results for "${query}"`
              : `${totalResults} result${totalResults !== 1 ? 's' : ''} for "${query}"`
            }
          </p>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-xl bg-light-card dark:bg-dark-card animate-pulse h-52" />
            ))}
          </div>
        )}

        {!isLoading && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab + q}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >

              {/* Empty state */}
              {totalResults === 0 && q && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 rounded-full bg-light-card dark:bg-dark-card flex items-center justify-center mb-4">
                    <Search size={28} className="text-light-subtle dark:text-dark-subtle" />
                  </div>
                  <p className="text-base font-semibold text-light-text dark:text-dark-text mb-1">Nothing found</p>
                  <p className="text-sm text-light-subtle dark:text-dark-subtle">Try a different keyword or browse the tabs</p>
                </div>
              )}

              {/* No query — prompt */}
              {!q && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 dark:bg-golden/10 flex items-center justify-center mb-4">
                    <Search size={28} className="text-primary dark:text-golden" />
                  </div>
                  <p className="text-base font-semibold text-light-text dark:text-dark-text mb-1">Search Majengo</p>
                  <p className="text-sm text-light-subtle dark:text-dark-subtle">Find properties, products & services across Kenya</p>
                </div>
              )}

              {/* Properties */}
              {showProperties && matchedVacancies.length > 0 && (
                <div className="mb-8">
                  {activeTab === 'all' && (
                    <h2 className="text-sm font-bold text-light-text dark:text-dark-text mb-3 flex items-center gap-2">
                      <Home size={14} className="text-primary dark:text-golden" /> Properties
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 dark:bg-golden/10 text-primary dark:text-golden font-semibold">
                        {matchedVacancies.length}
                      </span>
                    </h2>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {matchedVacancies.map(v => (
                      <PropertyCard
                        key={v.id}
                        id={v.id} title={v.title} location={v.location} price={v.price}
                        imageUrl={getPropertyImage(v)} bedrooms={v.bedrooms} bathrooms={v.bathrooms}
                        area={v.area} type={v.type} price_unit={v.price_unit}
                        isFavorite={favorites.has(v.id)}
                        onToggleFavorite={id => handleToggleFavorite(id, 'property')}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Products */}
              {showProducts && matchedProducts.length > 0 && (
                <div className="mb-8">
                  {activeTab === 'all' && (
                    <h2 className="text-sm font-bold text-light-text dark:text-dark-text mb-3 flex items-center gap-2">
                      <Zap size={14} className="text-primary dark:text-golden" /> Products
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 dark:bg-golden/10 text-primary dark:text-golden font-semibold">
                        {matchedProducts.length}
                      </span>
                    </h2>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {matchedProducts.map(p => (
                      <ProductCard
                        key={p.id}
                        product={formatProduct(p)}
                        isFavorite={favorites.has(p.id)}
                        onToggleFavorite={id => handleToggleFavorite(id, 'product')}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Services */}
              {showServices && matchedServices.length > 0 && (
                <div className="mb-8">
                  {activeTab === 'all' && (
                    <h2 className="text-sm font-bold text-light-text dark:text-dark-text mb-3 flex items-center gap-2">
                      <Wrench size={14} className="text-primary dark:text-golden" /> Services
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 dark:bg-golden/10 text-primary dark:text-golden font-semibold">
                        {matchedServices.length}
                      </span>
                    </h2>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {matchedServices.map(s => (
                      <ServiceCard
                        key={s.id}
                        service={formatService(s)}
                        isFavorite={favorites.has(s.id)}
                        onToggleFavorite={id => handleToggleFavorite(id, 'service')}
                      />
                    ))}
                  </div>
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
